import { roundedRect } from '@bn-games/render-helpers';
import { TILE_LONG, TILE_SHORT } from '../utils/constants.js';
import type { ChainRegion } from './BoardRenderer.js';
import type { RenderPalette } from './palette.js';
import { drawTileBack } from './TileRenderer.js';

/**
 * Geometry for the boneyard tile-stack rendered inside the chain region.
 * Tiles fan down-and-left from a fixed top anchor so the pile reads as a
 * deck of dominoes viewed from a slight angle. The pile shrinks visually
 * as the boneyard empties (3 size states) but the top tile — the click
 * target — stays anchored.
 *
 * The pile lives in the bottom-right corner of the chain region: the same
 * dead zone the old Draw button occupied. Muggins (when surfaced) sits
 * above the pile via `BONEYARD_STACK_MAX_HEIGHT`.
 */
export interface BoneyardStackLayout {
  /** Full bounding box of the pile (includes all visible tile backs). */
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  /** Per-tile rects, bottom-to-top so the top tile is drawn last. */
  readonly tileRects: readonly { readonly x: number; readonly y: number }[];
  /** Tile-back width. */
  readonly tileLong: number;
  /** Tile-back height. */
  readonly tileShort: number;
  /** Number of tile backs that will be drawn for the current count. */
  readonly visibleCount: number;
}

const STACK_INSET = 16;
/** Scale applied to the base tile size so the pile reads as a focal element. */
const TILE_SCALE = 1.25;
const TILE_LONG_SCALED = TILE_LONG * TILE_SCALE;
const TILE_SHORT_SCALED = TILE_SHORT * TILE_SCALE;
/** Per-tile offset from the one above — both axes, for a perspective stack. */
const STEP_X = 4;
const STEP_Y = 4;

/** Visible tile-back counts per size tier. */
const VISIBLE_LOW = 2;
const VISIBLE_MEDIUM = 4;
const VISIBLE_HIGH = 6;
/** Boneyard count thresholds for the three size states. */
const COUNT_HIGH_THRESHOLD = 9;
const COUNT_MEDIUM_THRESHOLD = 4;

/**
 * Total vertical space the pile may occupy at its largest state. Used by
 * `main.ts` to keep the Muggins button at a stable Y regardless of the
 * actual remaining-tile count.
 */
export const BONEYARD_STACK_MAX_HEIGHT =
  TILE_SHORT_SCALED + STEP_Y * (VISIBLE_HIGH - 1);

/**
 * Vertical gap between the top of the boneyard pile and the bottom of the
 * Muggins button. Callers add this to `BONEYARD_STACK_MAX_HEIGHT` when
 * computing how much bottom-of-chain space to reserve for the pile.
 */
export const BONEYARD_TO_MUGGINS_GAP = 8;

/**
 * Map a boneyard remaining-count to the number of tile-backs to render.
 * Three discrete states so the pile shrinks noticeably as tiles deplete
 * without flickering on every single draw.
 */
export function visibleTilesForCount(count: number): number {
  if (count <= 0) return 0;
  if (count >= COUNT_HIGH_THRESHOLD) return VISIBLE_HIGH;
  if (count >= COUNT_MEDIUM_THRESHOLD) return VISIBLE_MEDIUM;
  return Math.min(VISIBLE_LOW, count);
}

/**
 * Compute the boneyard pile's geometry for the given remaining count. The
 * top tile is anchored at a fixed corner so the click target stays put as
 * the pile shrinks; lower tiles fan down-and-left from there.
 *
 * Returns `null` when there are no tiles to draw.
 */
export function layoutBoneyardStack(
  chainRegion: ChainRegion,
  count: number,
): BoneyardStackLayout | null {
  const visible = visibleTilesForCount(count);
  if (visible === 0) return null;

  // Top tile's top-right corner is pinned: the pile always occupies the
  // same right edge / top-row Y regardless of state.
  const topTileRight = chainRegion.x + chainRegion.width - STACK_INSET;
  const topTileLeft = topTileRight - TILE_LONG_SCALED;
  // Reserve the max possible height so Muggins (which uses the same Y
  // budget) doesn't jump when the pile resizes.
  const topTileY =
    chainRegion.y +
    chainRegion.height -
    STACK_INSET -
    BONEYARD_STACK_MAX_HEIGHT;

  const tileRects: { x: number; y: number }[] = [];
  // i = 0 is the deepest (bottom) tile, i = visible-1 is the top tile.
  for (let i = 0; i < visible; i += 1) {
    const depth = visible - 1 - i; // 0 for top, larger for deeper tiles
    tileRects.push({
      x: topTileLeft - STEP_X * depth,
      y: topTileY + STEP_Y * depth,
    });
  }

  const width = TILE_LONG_SCALED + STEP_X * (visible - 1);
  const height = TILE_SHORT_SCALED + STEP_Y * (visible - 1);
  const x = topTileLeft - STEP_X * (visible - 1);
  const y = topTileY;

  return {
    x,
    y,
    width,
    height,
    tileRects,
    tileLong: TILE_LONG_SCALED,
    tileShort: TILE_SHORT_SCALED,
    visibleCount: visible,
  };
}

export interface DrawBoneyardStackOptions {
  /** Dashed outline around the pile when it's the local seat's turn to draw. */
  readonly glow: boolean;
  /** Numeric remaining-tile badge on the top-right of the pile. */
  readonly showCount: boolean;
  /** Faded appearance when the pile exists but the local player can't draw. */
  readonly dimmed: boolean;
}

const COUNT_BADGE_FONT = "700 12px 'Segoe UI', system-ui, sans-serif";
const COUNT_BADGE_RADIUS = 9;
const COUNT_BADGE_PAD_X = 6;
const COUNT_BADGE_HEIGHT = 18;
/** Padding from the pile bounding box to the dashed indicator rectangle. */
const INDICATOR_PADDING = 4;

export function drawBoneyardStack(
  ctx: CanvasRenderingContext2D,
  layout: BoneyardStackLayout,
  count: number,
  palette: RenderPalette,
  options: DrawBoneyardStackOptions,
): void {
  if (count <= 0 || layout.visibleCount <= 0) return;

  ctx.save();
  if (options.dimmed) ctx.globalAlpha = 0.7;

  // Bottom-to-top: the top tile (drawn last) is the one the player sees
  // and clicks.
  for (let i = 0; i < layout.visibleCount; i += 1) {
    const r = layout.tileRects[i];
    if (!r) continue;
    drawTileBack(
      ctx,
      {
        x: r.x,
        y: r.y,
        long: layout.tileLong,
        short: layout.tileShort,
        orientation: 'horizontal',
      },
      palette,
    );
  }

  if (options.glow) {
    // Dashed outline around the pile, drawn on top of the tiles. Mirrors
    // the legal-arm drop-slot indicator so "you can drop a tile here" and
    // "you can draw from this pile" share the same visual language.
    ctx.save();
    ctx.strokeStyle = palette.validArmGlow;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 5]);
    roundedRect(
      ctx,
      layout.x - INDICATOR_PADDING + 0.5,
      layout.y - INDICATOR_PADDING + 0.5,
      layout.width + INDICATOR_PADDING * 2 - 1,
      layout.height + INDICATOR_PADDING * 2 - 1,
      8,
    );
    ctx.stroke();
    ctx.restore();
  }

  if (options.showCount) {
    const topRect = layout.tileRects[layout.visibleCount - 1];
    if (topRect) {
      ctx.font = COUNT_BADGE_FONT;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const label = String(count);
      const textWidth = ctx.measureText(label).width;
      const badgeW = Math.max(20, textWidth + COUNT_BADGE_PAD_X * 2);
      const badgeX = topRect.x + layout.tileLong - badgeW / 2 - 4;
      const badgeY = topRect.y - COUNT_BADGE_HEIGHT / 2 + 4;
      ctx.save();
      ctx.shadowColor = 'rgba(0, 0, 0, 0.4)';
      ctx.shadowBlur = 4;
      ctx.shadowOffsetY = 1;
      roundedRect(ctx, badgeX, badgeY, badgeW, COUNT_BADGE_HEIGHT, COUNT_BADGE_RADIUS);
      ctx.fillStyle = palette.bannerBg;
      ctx.fill();
      ctx.restore();
      ctx.strokeStyle = palette.bannerBorder;
      ctx.lineWidth = 1;
      roundedRect(
        ctx,
        badgeX + 0.5,
        badgeY + 0.5,
        badgeW - 1,
        COUNT_BADGE_HEIGHT - 1,
        COUNT_BADGE_RADIUS,
      );
      ctx.stroke();
      ctx.fillStyle = palette.bannerTextColor;
      ctx.fillText(label, badgeX + badgeW / 2, badgeY + COUNT_BADGE_HEIGHT / 2);
    }
  }

  ctx.restore();
}

/**
 * Hit-test the pile's bounding box. Callers gate on `drawEnabled` before
 * dispatching the actual Draw action.
 */
export function hitTestBoneyardStack(
  layout: BoneyardStackLayout,
  x: number,
  y: number,
): boolean {
  return (
    x >= layout.x &&
    x <= layout.x + layout.width &&
    y >= layout.y &&
    y <= layout.y + layout.height
  );
}
